// Libs
import React, { useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { Button, ButtonGroup, ClickAwayListener, Grow, MenuList, Paper, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
// Styles, images, icons
import styles from './BaseSplitButton.module.scss';
import { icons } from '~/assets';

type Props = {
  isCheckToggle?: boolean;
  height?: number | string;
  primaryWidth?: number | string;
  label?: string;
  onClick?: () => void;
  children?: React.ReactNode;
};

const cx = classNames.bind(styles);

const BaseSplitButton = (props: Props) => {
  //#region Destructuring Props
  const { label, primaryWidth, height, onClick, children, isCheckToggle = true } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const anchorRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [open, setOpen] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };
  //#endregion Handle Function

  return (
    <React.Fragment>
      <ButtonGroup variant='contained' ref={anchorRef} className={cx('buttonGroup')}>
        <Button onClick={onClick} className={cx('btn')} style={{ height, width: primaryWidth }}>
          {label}
        </Button>
        {isCheckToggle && (
          <Button onClick={handleToggle} className={cx('btn')} style={{ height }}>
            <img
              src={icons.commonIconSplitButton}
              alt={t('common_alt_img')}
              className={cx('splitButtonIcon')}
            />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 1, width: anchorRef ? anchorRef.current?.offsetWidth : 'auto' }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ placement: 'bottom-end' }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper className={cx('paper')}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem className={cx('menuList')}>
                  {React.Children.map(children, (child) => {
                    if (!React.isValidElement(child)) return child;

                    const element = child as React.ReactElement<any>;
                    const originalOnClick = element.props.onClick;

                    return React.cloneElement(element, {
                      onClick: (event: React.MouseEvent<HTMLElement>) => {
                        setOpen(false);
                        if (originalOnClick) {
                          originalOnClick(event);
                        }
                      },
                    });
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

export default BaseSplitButton;
